import '../MetricsLayout.scss';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import SpinnerChidori from 'components/SpinnerChidori/SpinnerChidori';
import TimeSeriesChart from '../TimeSeriesChart/TimeSeriesChart';
import { SelectedRange } from 'types/types';
import JobsChart from 'components/JobsTable/JobsChart/JobsChart';
import { formatDurationToHMS } from 'utils/date';

import {
  useGetReservedMemory,
  useGetDiskMetrics,
  useGetMaxMemory,
  useGetNumberOfQueuedJobs,
  useGetNumberOfRunningJobs,
  useGetUsedMemory,
} from 'hooks/metrics';

type MetricsProps = {
  selectedMetric: string;
  selectedRange: SelectedRange;
  formatedRange: string;
  selectedTable: string | null;
  selectedSchema: string | null;
};
function Metrics({
  selectedRange,
  formatedRange,
  selectedMetric,
  selectedTable,
  selectedSchema,
}: MetricsProps) {
  const intl = useIntl();
  const cachedFormatDuration = useCallback(
    ({ duration, shorthand }: { duration: Duration; shorthand?: boolean }) => {
      return formatDurationToHMS({
        duration,
        shorthand: shorthand || false,
        useLeastLetters: true,
      });
    },
    []
  );

  const { data: runningJobs, isLoading: isNumberOfJobsLoading } =
    useGetNumberOfRunningJobs({
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      formatedRange,
    });

  const { data: maxMemory, isLoading: isMaxMemoryLoading } = useGetMaxMemory({
    fromDate: selectedRange.fromDate,
    toDate: selectedRange.toDate,
    formatedRange,
  });

  const { data: usedMemory, isLoading: isUsedMemoryLoading } = useGetUsedMemory(
    {
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      formatedRange,
    }
  );

  const { data: queuedJobs, isLoading: isQueuedJobsLoading } =
    useGetNumberOfQueuedJobs({
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      formatedRange,
    });

  const { data: diskMetrics, isLoading: isDiskMetricsLoading } =
    useGetDiskMetrics({
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      formatedRange,
    });

  const { data: reservedMemoryData, isLoading: isReservedMemoryLoading } =
    useGetReservedMemory({
      fromDate: selectedRange.fromDate,
      toDate: selectedRange.toDate,
      formatedRange,
    });

  //const bytesToMB = (bytes: number) => bytes / 1024 ** 2; not used but left it just in case
  const bytesToGB = (bytes: number) => bytes / 1024 ** 3;

  const maxMemoryPoints = maxMemory?.Points || [];
  const usedMemoryPoints = usedMemory?.Points || [];
  const reservedMemoryPoints = reservedMemoryData?.Points || [];
  const MemoryTimeData = reservedMemoryData?.TimeData || [];
  const detailedMemory = reservedMemoryData?.MemoryDetails || [];

  const diskCapacityInGB = diskMetrics?.Capacity.map(bytesToGB) || [];
  const diskUsageInGB = diskMetrics?.Usage.map(bytesToGB) || [];
  const diskTimeData = diskMetrics?.TimeData;

  const numberOfRunningJob = runningJobs?.Points || [];
  const jobsTimeData = runningJobs?.TimeData;

  const numberOfQueuedJob = queuedJobs?.Points || [];

  const hasDiskData = diskCapacityInGB.length > 0 || diskUsageInGB.length > 0;
  const hasRunningJobsData = numberOfRunningJob.length > 0;
  const hasQueuedJobsData = numberOfQueuedJob.length > 0;

  const renderChartContent = () => {
    if (
      (selectedMetric === 'sparkDriverMemory' &&
        (isMaxMemoryLoading ||
          isUsedMemoryLoading ||
          isReservedMemoryLoading)) ||
      (selectedMetric === 'numberOfJobs' && isNumberOfJobsLoading) ||
      (selectedMetric === 'numberOfQueuedJobs' && isQueuedJobsLoading) ||
      (selectedMetric === 'diskMetrics' && isDiskMetricsLoading)
    ) {
      return <SpinnerChidori />;
    }

    if (
      !isMaxMemoryLoading &&
      maxMemoryPoints.length === 0 &&
      selectedMetric === 'sparkDriverMemory'
    ) {
      return <div>No memory data available for the selected range</div>;
    }

    if (
      !hasRunningJobsData &&
      !isNumberOfJobsLoading &&
      selectedMetric === 'numberOfJobs'
    ) {
      return <div>No running jobs available for the selected range</div>;
    }

    if (
      !hasDiskData &&
      !isDiskMetricsLoading &&
      selectedMetric === 'diskMetrics'
    ) {
      return <div>No disk metrics available for selected range</div>;
    }

    if (
      !isMaxMemoryLoading &&
      !isUsedMemoryLoading &&
      !isReservedMemoryLoading &&
      reservedMemoryPoints &&
      maxMemoryPoints &&
      usedMemoryPoints &&
      selectedMetric === 'sparkDriverMemory'
    ) {
      return (
        <TimeSeriesChart
          key="sparkDriverMemoryUsage"
          chartType="smooth"
          chartTitle={intl.formatMessage({
            id: 'metrics.sparkDriverMemoryUsage',
          })}
          yAxisFormatter={(val: number) => `${val.toFixed(1)} MB`}
          yAxisTitle={`${intl.formatMessage({ id: 'metrics.memory' })} (MB)`}
          toolTipFormatter={(val: number) => `${val} MB`}
          seriesData={[
            {
              name: intl.formatMessage({ id: 'metrics.allocatedMemory' }),
              data: maxMemoryPoints,
            },
            {
              name: intl.formatMessage({ id: 'metrics.usedMemory' }),
              data: usedMemoryPoints,
            },
            {
              name: intl.formatMessage({ id: 'metrics.reservedMemory' }),
              data: reservedMemoryPoints,
            },
          ]}
          timeData={MemoryTimeData}
          reservedMemoryDetails={detailedMemory}
        />
      );
    }

    if (
      !isNumberOfJobsLoading &&
      !isQueuedJobsLoading &&
      hasRunningJobsData &&
      hasQueuedJobsData &&
      selectedMetric === 'numberOfJobs'
    ) {
      return (
        <TimeSeriesChart
          key="numberOfJobs"
          chartType="stepline"
          chartTitle={intl.formatMessage({
            id: 'metrics.numberOfJobs',
          })}
          yAxisFormatter={(val: number) => `${val.toFixed(0)} Jobs`}
          yAxisTitle={`${intl.formatMessage({
            id: 'metrics.numberOfJobs',
          })} (Jobs)`}
          toolTipFormatter={(val: number) => `${val} Jobs`}
          seriesData={[
            {
              name: intl.formatMessage({ id: 'metrics.runningJob' }),
              data: numberOfRunningJob,
              zIndex: 9999,
            },
            {
              name: intl.formatMessage({ id: 'metrics.queuedJobs' }),
              data: numberOfQueuedJob,
              zIndex: 1,
            },
          ]}
          timeData={jobsTimeData}
        />
      );
    }

    if (
      !isDiskMetricsLoading &&
      hasDiskData &&
      selectedMetric === 'diskMetrics'
    ) {
      return (
        <TimeSeriesChart
          key="diskMetrics"
          chartType="smooth"
          chartTitle={intl.formatMessage({
            id: 'metrics.chidoriDisk',
          })}
          yAxisFormatter={(val: number) => `${val.toFixed(1)} GB`}
          yAxisTitle={`${intl.formatMessage({ id: 'metrics.disk' })} (GB)`}
          toolTipFormatter={(val: number) => `${val} GB`}
          seriesData={[
            {
              name: intl.formatMessage({ id: 'metrics.disk.capacity' }),
              data: diskCapacityInGB,
            },
            {
              name: intl.formatMessage({ id: 'metrics.disk.usage' }),
              data: diskUsageInGB,
            },
          ]}
          timeData={diskTimeData}
        />
      );
    }
    if (selectedMetric === 'tableJobs') {
      if (!selectedTable || !selectedSchema) {
        return (
          <div className="metrics-layout__chart">
            <p>Please select a Schema and a Table</p>
          </div>
        );
      }

      return (
        <JobsChart
          tableName={selectedTable}
          schemaName={selectedSchema}
          cachedFormatDuration={cachedFormatDuration}
        />
      );
    }
  };

  return <div className="metrics-layout__chart">{renderChartContent()}</div>;
}

export default Metrics;
